import { useState, useEffect, useRef, ChangeEvent, FC } from 'react';
import { useFormik } from 'formik';
import { InferType } from 'yup';
import {
  Box,
  Button,
  Link,
  Typography,
  useTheme,
  useMediaQuery,
  SelectChangeEvent,
} from '@mui/material';
import TextField from 'src/components/InputFields/TextField';
import FormHeader from 'src/components/Headers/FormHeader';
import CountryPicker from 'src/components/InputFields/CountryPicker';
import StepperHeader from 'src/components/Headers/StepperHeader';
import LogoUpload from 'src/components/InputFields/LogoUpload';
import MobileField from 'src/components/InputFields/MobileField';
import { useNavigate } from 'react-router-dom';
import Header from 'src/components/Headers/Header';
import ExistingError from 'src/components/Errors/ErrorAlert';
import {
  signupValidationSchema,
  signupCompanySchema,
} from 'src/utils/validationScheme.utils';
import {
  apiUrls,
  dataStorageKeywords,
  errorMessages,
  help,
  routeURL,
  signupSettings,
} from 'src/utils/properties.utils';
import { axiosInstance } from 'src/utils/axiosInstance.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { signupInitialValues } from 'src/utils/initialValues.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  AuthFormContainer,
  AuthFieldsContainer,
  AuthButton,
  BackButton,
  AuthLinkContainer,
  ChevronIcon,
} from 'src/components/StyledComponents/StyledAuthPages';
import { setEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  checkEmailExists as checkEmailExistsService,
  checkRegNoExists as checkRegNoExistsService,
} from 'src/services/auth.service';
import { ButtonStyled } from 'src/components/StyledComponents/StyledPageComponents';

const steps = ['Company Email', 'Company Information', 'Profile Information'];

const Signup: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSnackbar } = StyledSnackbar();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [companymailContainerHeight, setCompanymailContainerHeight] =
    useState(400);
  const [companyContainerHeight, setCompanyContainerHeight] = useState(1050);
  const [profileContainerHeight, setProfileContainerHeight] = useState(800);
  const [error, setError] = useState({
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });
  const [isExistingErrorVisible, setIsExistingErrorVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(
    null
  );

  useEffect(() => {
    setIsExistingErrorVisible(!!error.message && error.message !== '');
  }, [error]);

  const checkEmailExists = async (email: string) => {
    try {
      const message = await checkEmailExistsService(email);
      message
        ? setError({ message, severity: 'info' })
        : setError({ message: '', severity: 'error' });
      return message || false;
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      setError({ message: errorMessage, severity: 'info' });
      return true;
    }
  };

  const checkRegNoExists = async (regNo: string) => {
    try {
      const message = await checkRegNoExistsService(regNo);
      if (message) {
        formik.setFieldError('companyReg', message);
        return true;
      }
      return false;
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      formik.setFieldError('companyReg', errorMessage);
      return true;
    }
  };

  const handleCountryChange = (event: SelectChangeEvent<string>) =>
    formik.setFieldValue('companyCountry', event.target.value);

  const resetTimer = () =>
    localStorage.removeItem('emailVerificationTimerEndTime');

  const handleSubmit = async (values: any) => {
    values.phone = `+${values.countryCode} ${values.phoneNumber}`;

    if (activeStep === 0) {
      const emailExists = await checkEmailExists(values.email);
      emailExists
        ? setError({ message: errorMessages.COMPANY_EXISTS, severity: 'error' })
        : handleNext();
    } else if (activeStep === 1) {
      const regNoExists = await checkRegNoExists(values.companyReg);
      if (!regNoExists) {
        handleNext();
      }
    } else if (activeStep === steps.length - 1) {
      const formData = new FormData();
      const castedCompanyValues = signupCompanySchema.cast(values) as InferType<
        typeof signupCompanySchema
      >;
      formData.append('email', values.email);
      formData.append('name', values.companyName);
      formData.append('registrationNo', values.companyReg);
      formData.append('country', values.companyCountry);
      formData.append('addressLine1', values.addressLine1);
      if (castedCompanyValues.addressLine2) {
        formData.append('addressLine2', castedCompanyValues.addressLine2);
      }
      formData.append('postalCode', values.postalCode);
      formData.append('city', values.city);
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('mobileNo', values.phone);
      formData.append('password', values.password);
      if (selectedFile) formData.append('logo', selectedFile);

      try {
        await axiosInstance.post(apiUrls.SIGNUP, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setEncryptedLocalStorageData(
          dataStorageKeywords.USER_EMAIL,
          values.email
        );
        resetTimer();
        navigate(routeURL.VERIFY_EMAIL);
      } catch (error: any) {
        if (error.response?.data) {
          console.error('Sign-up error response:', error.response.data.error);
        } else {
          console.error('Error registering user:', error);
        }
      }
    } else {
      handleNext();
    }
  };

  const formik = useFormik({
    initialValues: signupInitialValues,
    validationSchema: signupValidationSchema[activeStep],
    onSubmit: (values) => handleSubmit(values),
  });

  useEffect(() => {
    const errorCount = Object.keys(formik.errors).length;
    const additionalHeight =
      activeStep === 0 && isExistingErrorVisible ? 30 : 0;
    const baseHeight = activeStep === 0 ? 400 : activeStep === 1 ? 1100 : 800;
    const newHeight = baseHeight + errorCount * 30 + additionalHeight;

    if (activeStep === 0) setCompanymailContainerHeight(newHeight);
    else if (activeStep === 1) setCompanyContainerHeight(newHeight);
    else setProfileContainerHeight(newHeight);
  }, [formik.errors, isExistingErrorVisible, activeStep]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      // Reset submitCount and touched so the next step's fields don't
      // immediately show validation errors from the previous step's submit.
      formik.resetForm({ values: formik.values });
    } else {
      handleSubmit(formik.values);
    }
  };

  const handleBack = () =>
    setActiveStep((prevActiveStep) => prevActiveStep - 1);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 2) {
      showSnackbar(help.FILE_SIZE_BIG, 'error');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#FFFFFF"
      p={2}
    >
      <Header smScreen={smScreen} />

      {activeStep === 0 && (
        <AuthFormContainer
          smScreen={smScreen}
          height={companymailContainerHeight}
          component="form"
          onSubmit={formik.handleSubmit as any}
        >
          <FormHeader
            title={signupSettings.CREATE_ACCOUNT_TITLE}
            subtitle={signupSettings.COMPANY_EMAIL_SUBTITLE}
          />

          {error.message && error.message !== '' && (
            <ExistingError message={error.message} severity={error.severity} />
          )}

          <AuthFieldsContainer smScreen={smScreen}>
            <TextField
              id="email"
              label="Company Email"
              type="email"
              formikProps={formik}
              errorState={isExistingErrorVisible}
            />
          </AuthFieldsContainer>

          <AuthButton smScreen={smScreen}>
            <ButtonStyled fullWidth type="submit">
              Continue
            </ButtonStyled>
          </AuthButton>

          <AuthLinkContainer smScreen={smScreen}>
            <Typography
              fontSize="14px"
              fontFamily="Outfit"
              fontWeight="300"
              color="#0B2330"
            >
              <Link
                href={routeURL.LOGIN}
                underline="hover"
                color="var(--subtext)"
                fontSize="14px"
                fontFamily="Lufga"
                fontWeight="500"
              >
                Log into an existing account
              </Link>
            </Typography>
          </AuthLinkContainer>
        </AuthFormContainer>
      )}

      {activeStep === 1 && (
        <AuthFormContainer
          smScreen={smScreen}
          height={companyContainerHeight}
          component="form"
          onSubmit={formik.handleSubmit as any}
        >
          <BackButton className="btn-back" onClick={handleBack}>
            <ChevronIcon className="bx bx-chevron-left"></ChevronIcon>
          </BackButton>

          <StepperHeader
            title={signupSettings.CREATE_ACCOUNT_TITLE}
            subtitle={signupSettings.COMPANY_INFO_SUBTITLE}
          />

          <AuthFieldsContainer smScreen={smScreen}>
            <LogoUpload
              imagePreview={imagePreview}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onClick={handleUploadClick}
            />

            <Box mt={2}>
              <TextField
                id="companyName"
                label="Company Name"
                type="text"
                formikProps={formik}
              />

              <TextField
                id="companyReg"
                label="Company Reg. No"
                type="text"
                formikProps={formik}
              />

              <TextField
                id="addressLine1"
                label="Address Line 1"
                type="text"
                formikProps={formik}
              />

              <TextField
                id="addressLine2"
                label="Address Line 2"
                type="text"
                formikProps={formik}
              />

              <TextField
                id="postalCode"
                label="Postal Code"
                type="text"
                formikProps={formik}
              />

              <TextField
                id="city"
                label="City"
                type="text"
                formikProps={formik}
              />

              <CountryPicker
                label="Country"
                value={formik.values.companyCountry}
                onChange={handleCountryChange}
                error={formik.errors.companyCountry}
                touched={formik.touched.companyCountry}
              />
            </Box>
          </AuthFieldsContainer>

          <AuthButton smScreen={smScreen}>
            <Button className="btn-primary" fullWidth type="submit">
              Continue
            </Button>
          </AuthButton>
        </AuthFormContainer>
      )}

      {activeStep === 2 && (
        <AuthFormContainer
          smScreen={smScreen}
          height={profileContainerHeight}
          component="form"
          onSubmit={formik.handleSubmit as any}
        >
          <BackButton className="btn-back" onClick={handleBack}>
            <ChevronIcon className="bx bx-chevron-left"></ChevronIcon>
          </BackButton>

          <StepperHeader
            title={signupSettings.CREATE_ACCOUNT_TITLE}
            subtitle={signupSettings.PROFILE_INFO_SUBTITLE}
          />

          <AuthFieldsContainer smScreen={smScreen}>
            <Box mt={2}>
              <TextField
                id="firstName"
                label="First Name"
                type="text"
                formikProps={formik}
              />
            </Box>

            <Box mt={2}>
              <TextField
                id="lastName"
                label="Last Name"
                type="text"
                formikProps={formik}
              />
            </Box>

            <MobileField
              id="mobile"
              label="Mobile Number"
              formikProps={formik}
            />

            <TextField
              id="password"
              label="Password"
              type="password"
              showPasswordToggle
              formikProps={formik}
            />

            <TextField
              id="repassword"
              label="Confirm Password"
              type="password"
              showPasswordToggle
              formikProps={formik}
            />
          </AuthFieldsContainer>

          <AuthButton smScreen={smScreen}>
            <Button className="btn-primary" fullWidth type="submit">
              Submit
            </Button>
          </AuthButton>
        </AuthFormContainer>
      )}
    </Box>
  );
};

export default Signup;
